/**
 * ui/components/shell/update-banner.ts
 *
 * "A new version is ready" bar shown by `UpdatePromptController`.
 */
import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { tokens } from '../../styles/tokens.js';
import { posterControls } from '../../styles/poster.js';
import { updatePrompt, type UpdatePromptController } from '../../../pwa/update-prompt.js';
import { DB_BLOCKED_EVENT, DB_OUTDATED_EVENT } from '../../../adapters/indexed-db.js';

@customElement('update-banner')
export class UpdateBanner extends LitElement {
  static styles = [
    tokens,
    css`
      :host {
        position: fixed;
        left: 50%;
        bottom: 16px;
        transform: translateX(-50%);
        z-index: 1000;
        max-width: calc(100vw - 32px);
      }

      .bar {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 10px 14px;
        border-radius: var(--radius-sm);
        background: var(--color-surface);
        border: 1px solid var(--color-border);
        box-shadow: 0 6px 24px rgba(0, 0, 0, 0.18);
        font-size: 14px;
        color: var(--color-text);
      }

      button {
        font: inherit;
        font-size: 13px;
        padding: 4px 12px;
        border-radius: var(--radius-full);
        border: 1px solid var(--color-border);
        background: transparent;
        color: inherit;
        cursor: pointer;
      }

      button.primary {
        background: var(--color-primary);
        border-color: var(--color-edge);
        color: var(--color-on-primary, #fff);
      }
    `,
    posterControls,
  ];

  @property({ attribute: false }) controller: UpdatePromptController = updatePrompt;
  @state() private _visible = false;
  @state() private _dbBlocked = false;
  @state() private _dbOutdated = false;
  private _unsubscribe?: () => void;

  private readonly _onDbBlocked = () => {
    this._dbBlocked = true;
  };

  private readonly _onDbOutdated = () => {
    this._dbOutdated = true;
  };

  connectedCallback() {
    super.connectedCallback();
    this._unsubscribe = this.controller.subscribe((visible) => {
      this._visible = visible;
    });
    window.addEventListener(DB_BLOCKED_EVENT, this._onDbBlocked);
    window.addEventListener(DB_OUTDATED_EVENT, this._onDbOutdated);
  }

  disconnectedCallback() {
    this._unsubscribe?.();
    window.removeEventListener(DB_BLOCKED_EVENT, this._onDbBlocked);
    window.removeEventListener(DB_OUTDATED_EVENT, this._onDbOutdated);
    super.disconnectedCallback();
  }

  render() {
    if (this._dbOutdated) {
      // Nothing can be saved until this tab runs the newer version.
      return html`
        <div class="bar" role="alert" data-db-outdated>
          <span>This app was updated in another tab. Reload to keep saving your progress.</span>
          <button class="primary" @click=${() => location.reload()}>Reload</button>
        </div>
      `;
    }
    if (this._dbBlocked) {
      return html`
        <div class="bar" role="alert" data-db-blocked>
          <span>This app is open in another tab with an older version. Close that tab to finish updating.</span>
          <button @click=${() => { this._dbBlocked = false; }}>OK</button>
        </div>
      `;
    }
    if (!this._visible) return nothing;
    return html`
      <div class="bar" role="status">
        <span>A new version is ready.</span>
        <button class="primary" data-reload @click=${() => this.controller.apply()}>Reload</button>
        <button data-later @click=${() => this.controller.dismiss()}>Later</button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'update-banner': UpdateBanner;
  }
}
